<template>
  <div class="api-docs">
    <div class="api-docs-content">
      <div class="api-docs-header">
        <img src="/assets/Logo.png" alt="Logo" class="api-docs-logo" />
      </div>

      <div class="doc-box doc-note">
        <i class="fa fa-info-circle"></i>
        <p>
          <strong>Importante:</strong> el usuario API <strong>no puede generar la guía</strong>. Solo puede cotizar el envío y elegir la transportadora.
          La guía la genera internamente el equipo desde el panel; después de eso, el estado del pedido trae el link de seguimiento.
        </p>
      </div>

      <div class="flow">
        <div v-for="(step, index) in steps" :key="step.title" class="step" :class="{ internal: step.internal }">
          <div class="step-num">{{ index + 1 }}</div>
          <details class="step-card" :open="index === 0">
            <summary>
              <span class="step-title">{{ step.title }}</span>
              <span v-if="step.method" class="step-route">
                <span class="method" :class="step.method.toLowerCase()">{{ step.method }}</span>
                <span class="path">{{ step.path }}</span>
              </span>
              <span v-else class="tag tag-no">Interno: lo hace el equipo desde el panel</span>
            </summary>
            <div class="step-body">
              <p v-html="step.description"></p>
              <template v-for="block in step.blocks" :key="block.label">
                <div class="block-label">{{ block.label }}</div>
                <pre>{{ block.code }}</pre>
              </template>
              <p v-if="step.footer" v-html="step.footer"></p>
            </div>
          </details>
        </div>
      </div>

      <div class="doc-box">
        <h2>Qué puede hacer el usuario API</h2>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr><th>Acción</th><th>Permitido</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in permissions" :key="row.action">
                <td>{{ row.action }}</td>
                <td><span class="tag" :class="{ 'tag-no': !row.allowed }">{{ row.label }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="doc-box">
        <h2>Errores comunes</h2>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr><th>Código</th><th>Significado</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in errors" :key="row.code">
                <td><code>{{ row.code }}</code></td>
                <td>{{ row.text }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p class="api-docs-footer">Origen de los envíos: Cúcuta. No se cotiza hacia Cúcuta, Los Patios ni Villa del Rosario.</p>
    </div>
  </div>
</template>

<script>
const body = document.getElementsByTagName("body")[0];

const SELECTED_CARRIER = `"selected_carrier": {
    "carrier_id": "6736c7ec521a1e796652e103",
    "carrier_name": "Interrapidísimo",
    "carrier_image": "https://.../logo.png",
    "shipping_cost": 12500,
    "delivery_days": 2,
    "selected_at": "2026-10-29T09:40:12-05:00"
  }`;

// Documentación pública de la API de integraciones (solo accesible por URL)
export default {
  name: "ApiDocs",
  data() {
    return {
      steps: [
        {
          title: "Consultar productos",
          method: "GET",
          path: "/api/products",
          description: "Lista los productos con stock. Usa <code>search</code> para buscar por nombre, SKU o código de barras, o consulta uno por SKU con <code>GET /api/products/{sku}</code>.",
          blocks: [
            {
              label: "Petición",
              code: `curl -H "Authorization: Bearer $TOKEN" -H "Accept: application/json" \\
  "https://{host}/api/products?search=77024341002"`,
            },
            {
              label: "Respuesta 200",
              code: `{
  "search": "77024341002",
  "count": 1,
  "data": [
    { "id": 2052, "name": "212 MEN NYC", "ref": "77024341002",
      "quantity": 18, "price": 54000 }
  ]
}`,
            },
          ],
          footer: "Guarda el <code>id</code> del producto: es el <code>inventory_id</code> del pedido.",
        },
        {
          title: "Crear el pedido",
          method: "POST",
          path: "/api/sales",
          description: "Crea la venta, descuenta stock y registra el pago. <code>payment_method</code>: <code>TRANSFERENCIA</code> o <code>CONTRAENTREGA</code>.",
          blocks: [
            {
              label: "Petición",
              code: `curl -X POST "https://{host}/api/sales" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Accept: application/json" -H "Content-Type: application/json" \\
  -d '{
    "client": {
      "name": "Ana Pérez", "document_type": "CC", "document": "1090123456",
      "phone": "3001234567", "email": "ana@correo.com",
      "address": "Calle 10 # 5-20", "departamento": "Bogotá D.C.", "municipio": "Bogotá"
    },
    "items": [ { "inventory_id": 2052, "quantity": 1, "unit_price": 54000 } ],
    "payment_method": "CONTRAENTREGA"
  }'`,
            },
            {
              label: "Respuesta 201 (resumida)",
              code: `{ "id": 335, "status": "aceptado", "order_status": "pendiente", "total": 54000, ... }`,
            },
          ],
          footer: "Los datos de <code>client</code> son los <strong>datos de envío</strong> del pedido. Para poder cotizar deben venir <code>name</code>, <code>phone</code>, <code>address</code>, <code>departamento</code> y <code>municipio</code> (con su nombre oficial, ej. <code>Medellín</code> / <code>Antioquia</code>). Guarda el <code>id</code>: es el <code>order_id</code> de los pasos siguientes.",
        },
        {
          title: "Cotizar el envío",
          method: "POST",
          path: "/api/public/shipping/quote",
          description: "Valida los datos de envío del pedido (el <code>client</code> de la venta) y devuelve las transportadoras disponibles con su precio. Peso, medidas y valor declarado son opcionales (por defecto 0.2 kg, 1×1×1 cm y el total del pedido).",
          blocks: [
            {
              label: "Petición",
              code: `curl -X POST "https://{host}/api/public/shipping/quote" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Accept: application/json" -H "Content-Type: application/json" \\
  -d '{
    "order_id": 335,
    "weight": 1, "length": 20, "width": 15, "height": 10,
    "payment_method": "Contraentrega"
  }'`,
            },
            {
              label: "Respuesta 200",
              code: `{
  "order_id": 335,
  "recipient_city": "11001000",
  "carriers": [
    { "carrier_id": "6736c7ec521a1e796652e103", "carrier_name": "Interrapidísimo",
      "carrier_image": "https://.../logo.png", "price": 12500, "delivery_days": 2 }
  ]
}`,
            },
          ],
          footer: "<code>payment_method</code> opcional: vacío = flete pago, <code>Contraentrega</code>, <code>Contraentrega + Flete Gratis</code>, <code>Flete Contraentrega</code>. Si al pedido le faltan datos de envío o el municipio no existe en el departamento, responde <code>422</code> con el detalle en <code>errors</code>.",
        },
        {
          title: "Elegir la transportadora",
          method: "PUT",
          path: "/api/public/orders/{order_id}/carrier",
          description: "Envía la opción elegida del paso 3 en <code>selected_carrier</code>. Se guarda en el pedido como referencia de lo que se le cotizó; no genera la guía. Obligatorios: <code>carrier_id</code> y <code>shipping_cost</code>.",
          blocks: [
            {
              label: "Petición",
              code: `curl -X PUT "https://{host}/api/public/orders/335/carrier" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Accept: application/json" -H "Content-Type: application/json" \\
  -d '{
    "order_id": 335,
    ${SELECTED_CARRIER.replace(/\n/g, "\n  ")}
  }'`,
            },
            {
              label: "Respuesta 200",
              code: `{
  "order_id": 335,
  ${SELECTED_CARRIER}
}`,
            },
          ],
          footer: "Se puede enviar de nuevo para cambiar la transportadora mientras el pedido no tenga guía.",
        },
        {
          title: "Generación de la guía",
          internal: true,
          description: "El usuario API <strong>no tiene ruta para generar la guía</strong>. El equipo la genera desde el detalle del pedido en el panel. Cuando se genera, el pedido queda con <code>waybill_id</code> y link de seguimiento, y ya no se pueden cambiar los datos de envío ni la transportadora (<code>409</code>).",
          blocks: [],
        },
        {
          title: "Consultar estado del pedido",
          method: "GET",
          path: "/api/public/orders/{order_id}/status",
          description: "Consulta en cualquier momento. Si ya se generó la guía, <code>shipping_generated</code> es <code>true</code> y <code>tracking_url</code> trae el link de seguimiento. Si no, ambos indican que falta la guía. <code>selected_carrier</code> es la transportadora elegida en el paso 4; <code>carrier_name</code> y <code>shipping_cost</code> son los de la guía generada internamente.",
          blocks: [
            {
              label: "Petición",
              code: `curl -H "Authorization: Bearer $TOKEN" -H "Accept: application/json" \\
  "https://{host}/api/public/orders/335/status"`,
            },
            {
              label: "Respuesta 200: con guía generada",
              code: `{
  "order_id": 335,
  "order_status": "enviado",
  "order_status_label": "ENVIADO",
  "shipping_generated": true,
  "waybill_id": "6736c7ec521a1e796652e103",
  "tracking_url": "https://app.conenvios.com/track/6736c7ec521a1e796652e103",
  ${SELECTED_CARRIER},
  "carrier_name": "Interrapidísimo",
  "shipping_cost": 12800,
  "shipped_at": "2026-10-23T10:56:33-05:00"
}`,
            },
            {
              label: "Respuesta 200: sin guía todavía",
              code: `{ "order_id": 335, "order_status": "pendiente", "shipping_generated": false,
  "waybill_id": null, "tracking_url": null, ... }`,
            },
          ],
          footer: "Estados: <code>pendiente</code>, <code>aceptado</code>, <code>facturado</code>, <code>enviado</code>, <code>en_camino</code>, <code>en_reparto</code>, <code>entregado</code>, <code>cancelado</code>, <code>devolucion</code>, <code>oficina_inter</code>.",
        },
      ],
      permissions: [
        { action: "Consultar productos", allowed: true, label: "Sí" },
        { action: "Crear pedidos", allowed: true, label: "Sí" },
                { action: "Cotizar envío y elegir transportadora", allowed: true, label: "Sí" },
        { action: "Consultar estado y seguimiento", allowed: true, label: "Sí" },
        { action: "Generar guía", allowed: false, label: "No (interno)" },
        { action: "Iniciar sesión / usar el panel", allowed: false, label: "No (403)" },
      ],
      errors: [
        { code: "401", text: "Falta el token o es inválido." },
        { code: "403", text: "Ruta del panel, no permitida al usuario API." },
        { code: "404", text: "El pedido no existe o es de otro vendedor." },
        { code: "409", text: "El pedido ya tiene guía; no se pueden cambiar envío ni transportadora." },
        { code: "422", text: "Datos inválidos, stock insuficiente o datos de envío del pedido incompletos." },
      ],
    };
  },
  created() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showNavbar = true;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    body.classList.add("bg-gray-100");
  },
};
</script>

<style scoped>
.api-docs {
  min-height: 100vh;
  background: #f8f9fa;
  color: #344767;
  padding: 32px 16px 64px;
}
.api-docs-content {
  max-width: 880px;
  margin: 0 auto;
}
.api-docs-header {
  text-align: center;
  margin-bottom: 24px;
}
.api-docs-logo {
  max-height: 56px;
  margin-bottom: 12px;
}
.api-docs-header h1 {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3a59;
  margin-bottom: 6px;
}
.lead {
  font-size: 0.95rem;
  color: #67748e;
  margin: 0;
}

.doc-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(44, 58, 89, 0.06);
  padding: 16px 20px;
  margin-bottom: 16px;
}
.doc-box h2 {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3a59;
  margin-bottom: 8px;
}
.doc-note {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fff8e6;
  border-left: 4px solid #f9b000;
}
.doc-note i {
  color: #f9b000;
  font-size: 1.1rem;
  margin-top: 3px;
}
.doc-note p {
  margin: 0;
  font-size: 0.9rem;
}

code {
  font-size: 0.85em;
  color: #2c3a59;
  background: #eef1f6;
  padding: 1px 5px;
  border-radius: 4px;
}

.flow {
  margin: 28px 0 12px;
}
.step {
  position: relative;
  padding-left: 54px;
}
.step::before {
  content: "";
  position: absolute;
  left: 19px;
  top: 44px;
  bottom: -4px;
  width: 2px;
  background: #dee2e6;
}
.step:last-child::before {
  display: none;
}
.step-num {
  position: absolute;
  left: 0;
  top: 6px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #2c3a59;
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 700;
  box-shadow: 0 0 0 4px #f8f9fa;
}
.step.internal .step-num {
  background: #8392ab;
}
.step-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(44, 58, 89, 0.06);
  margin-bottom: 18px;
  overflow: hidden;
}
.step.internal .step-card {
  border: 2px dashed #cbd3df;
  box-shadow: none;
}
.step-card summary {
  list-style: none;
  cursor: pointer;
  padding: 14px 18px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}
.step-card summary::-webkit-details-marker {
  display: none;
}
.step-card summary::after {
  content: "\25B8";
  margin-left: auto;
  color: #8392ab;
  transition: transform 0.15s;
}
.step-card[open] summary::after {
  transform: rotate(90deg);
}
.step-title {
  font-weight: 700;
  color: #2c3a59;
}
.step-route {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.method {
  font: 700 0.7rem/1 monospace;
  padding: 4px 7px;
  border-radius: 4px;
  color: #fff;
}
.method.get {
  background: #2dce89;
}
.method.post {
  background: #2c3a59;
}
.method.put {
  background: #f9b000;
}
.path {
  font-family: monospace;
  font-size: 0.82rem;
  color: #67748e;
  word-break: break-all;
}
.step-body {
  padding: 4px 18px 16px;
  border-top: 1px solid #e9ecef;
  font-size: 0.9rem;
}
.step-body p {
  margin: 12px 0 6px;
}
.block-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  color: #8392ab;
  margin: 14px 0 4px;
}
pre {
  margin: 0;
  background: #f4f6fa;
  border: 1px solid #e3e8ef;
  color: #2c3a59;
  padding: 12px 14px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.8rem;
  line-height: 1.5;
}

.tag {
  font-size: 0.72rem;
  padding: 3px 9px;
  border-radius: 999px;
  background: #e6f8f0;
  color: #1a9e64;
  font-weight: 700;
}
.tag-no {
  background: #fff3d6;
  color: #b37e00;
}
.table th {
  font-size: 0.75rem;
  color: #8392ab;
  font-weight: 700;
  text-transform: uppercase;
}
.table td {
  font-size: 0.88rem;
  color: #344767;
}

.api-docs-footer {
  text-align: center;
  color: #8392ab;
  font-size: 0.85rem;
  margin-top: 24px;
}
</style>
